// Bottom slide-up panel shown when the dispatcher clicks a flight in
// the Gates view. Two columns:
//
//   LEFT  — DOWNLOAD: what's coming off this aircraft? Which drivers
//            are pulling its bags, and where are they going?
//   RIGHT — DEPARTURE: what's going on this aircraft for its outbound?
//            Includes the local bags + every transfer feeder (other
//            gates' inbounds whose runs have stops pointing here),
//            with the driver bringing them.
//
// Closed via the X button or by clicking outside (handled by the
// caller setting selectedInboundId to null).

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import {
  BANK_BY_ID, RUNS_BY_ID, feedersForOutbound,
} from './mockBank';
import { SEED_ASSIGNMENTS } from './mockAssignments';
import { ROSTER } from './mockRoster';
import { C } from './styles';

// driverId → driver record lookup (built once).
const DRIVER_BY_ID = new Map(ROSTER.AM.map(d => [d.id, d]));

// Find every driver assigned to a run from the given inbound (the
// download crew for this flight). Returns objects keyed for display.
function downloadCrewForInbound(inboundId) {
  const inbound = BANK_BY_ID.get(inboundId);
  if (!inbound) return [];
  const runIdToDriver = new Map();
  for (const [driverId, list] of SEED_ASSIGNMENTS) {
    for (const item of list) {
      const run = RUNS_BY_ID.get(item.runId);
      if (run && run.inboundId === inboundId) {
        runIdToDriver.set(item.runId, { driverId, slotLabel: item.slotLabel });
      }
    }
  }
  // Pair the inbound's own runs (in slot order) with their assigned driver.
  return inbound.runs.map(run => {
    const assignment = runIdToDriver.get(run.id);
    const driver = assignment ? DRIVER_BY_ID.get(assignment.driverId) : null;
    return {
      runId: run.id,
      slotLabel: run.slotLabel,
      splitType: run.splitType,
      isFreightOnly: run.isFreightOnly,
      totalBags: run.totalBags,
      stops: run.stops,
      driver,    // may be null if not yet assigned
    };
  });
}

// Lookup the driver currently assigned to a specific runId.
function driverForRunId(runId) {
  for (const [driverId, list] of SEED_ASSIGNMENTS) {
    for (const item of list) {
      if (item.runId === runId) return DRIVER_BY_ID.get(driverId) || null;
    }
  }
  return null;
}

// All sources that feed bags onto this outbound. The inbound itself
// is the first "feeder" (originating local bags); the rest come from
// other gates via transfer drivers.
function departureSourcesForInbound(inbound) {
  // Other inbounds whose runs have a stop with this flight number.
  const feeders = feedersForOutbound(inbound.flightNumber)
    // Drop self — only show OTHER gates as transfer feeders.
    .filter(f => f.inboundId !== inbound.id)
    .map(f => ({
      ...f,
      driver: driverForRunId(f.runId),
    }));
  return feeders;
}

export default function FlightDetailPanel({ inboundId, onClose }) {
  const inbound = BANK_BY_ID.get(inboundId);

  // Drag state — { top, left } in CSS pixels relative to the GateView's
  // positioned ancestor. Null until the user actually drags; until then
  // the card snaps to the default top-right via CSS only.
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);              // {grabX, grabY, originX, originY}
  const cardRef = useRef(null);

  const onDragStart = (e) => {
    if (!cardRef.current) return;
    // Mouse-coordinate offset within the card so the corner under the
    // cursor stays fixed while dragging.
    const rect = cardRef.current.getBoundingClientRect();
    dragRef.current = {
      grabX: e.clientX, grabY: e.clientY,
      originX: rect.left, originY: rect.top,
      width: rect.width, height: rect.height,
    };
    e.preventDefault();
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const d = dragRef.current;
      // The parent ancestor for `position: absolute` is the GateView
      // outer wrapper. Convert viewport coords to parent-local by
      // subtracting the parent's bounding-rect origin.
      const parent = cardRef.current?.offsetParent;
      const parentRect = parent ? parent.getBoundingClientRect() : { left: 0, top: 0 };
      const dx = e.clientX - d.grabX;
      const dy = e.clientY - d.grabY;
      let newLeft = (d.originX + dx) - parentRect.left;
      let newTop  = (d.originY + dy) - parentRect.top;
      // Clamp inside the parent so the card can't be dragged off-screen.
      if (parent) {
        newLeft = Math.max(4, Math.min(newLeft, parent.clientWidth  - d.width  - 4));
        newTop  = Math.max(4, Math.min(newTop,  parent.clientHeight - d.height - 4));
      }
      setPos({ top: newTop, left: newLeft });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  if (!inbound) return null;

  const downloadCrew = downloadCrewForInbound(inboundId);
  const departureFeeders = departureSourcesForInbound(inbound);
  const flightNum = (inbound.flightNumber || '').replace(/^WN\s*/, '');
  const isOriginator = inbound.isOriginator;
  const isTerminator = inbound.isTerminator;

  // Default position = pinned top-right. Once the user drags, switch
  // to absolute top/left coordinates.
  const positionStyle = pos
    ? { top: pos.top, left: pos.left }
    : { top: 8, right: 12 };

  return (
    // Floating, draggable card. No backdrop, so the dispatcher can
    // still see and scroll the gate rows underneath. Close via the X
    // button or Esc (handled by the GateView Esc listener). Grab the
    // header to move the card around the view.
    <Box
      ref={cardRef}
      sx={{
        position: 'absolute',
        ...positionStyle,
        width: 'min(540px, 46vw)',
        maxHeight: 'calc(100% - 16px)',
        backgroundColor: 'rgba(30, 30, 30, 0.85)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        border: `1px solid ${C.borderStrong}`,
        borderRadius: 2,
        boxShadow: 'inset 0 0 18px rgba(17, 25, 36, 0.25), 0 16px 32px rgba(0, 0, 0, 0.55)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 20,
        animation: pos ? 'none' : 'cardPopIn 220ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        '@keyframes cardPopIn': {
          '0%':   { transform: 'translateY(-8px) scale(0.97)', opacity: 0 },
          '100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
        },
      }}
    >
      {/* Card header — also serves as the drag handle. */}
      <Box
        onMouseDown={onDragStart}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.25,
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: 'rgba(0, 0, 0, 0.25)',
          flexShrink: 0,
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <Tooltip title="Drag to move">
          <DragIndicatorIcon sx={{
            fontSize: 18, color: C.textMuted, ml: -0.5,
            '&:hover': { color: C.textSecondary },
          }} />
        </Tooltip>
        <Box sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.95rem', fontWeight: 'bold',
          color: C.textPrimary,
          backgroundColor: C.surfaceAlt,
          border: `1px solid ${C.border}`,
          px: 1, py: 0.4, borderRadius: 0.75,
        }}>
          {inbound.arrivalGate}
        </Box>
        <Typography sx={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '1.1rem', fontWeight: 'bold', color: C.textPrimary,
        }}>
          WN {flightNum}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: C.textSecondary }}>
          {inbound.from} → {inbound.eta}
          {isOriginator && (
            <Box component="span" sx={{ ml: 1, color: C.accent, fontWeight: 'bold' }}>
              · ORIGINATOR
            </Box>
          )}
          {isTerminator && (
            <Box component="span" sx={{ ml: 1, color: C.overnight, fontWeight: 'bold' }}>
              · TERMINATOR
            </Box>
          )}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Close (Esc)">
          <IconButton
            size="small"
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ color: C.textSecondary, cursor: 'pointer' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Two-column body */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* LEFT — DOWNLOAD */}
        <DownloadColumn
          inbound={inbound}
          crew={downloadCrew}
          isOriginator={isOriginator}
        />
        {/* RIGHT — DEPARTURE */}
        <DepartureColumn
          inbound={inbound}
          feeders={departureFeeders}
          isTerminator={isTerminator}
        />
      </Box>
    </Box>
  );
}

// ── Left column: download side ────────────────────────────────────

function DownloadColumn({ inbound, crew, isOriginator }) {
  const totalBags = crew.reduce((t, run) => t + (run.totalBags || 0), 0);
  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      display: 'flex', flexDirection: 'column',
      borderRight: `1px solid ${C.border}`,
    }}>
      <ColumnHeader
        icon={<FlightLandIcon sx={{ fontSize: 18, color: C.accent }} />}
        title="Download"
        subtitle={isOriginator
          ? 'Aircraft RON\'d — nothing to unload'
          : `${crew.length} run${crew.length === 1 ? '' : 's'} · ${totalBags} bag${totalBags === 1 ? '' : 's'}`}
      />
      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.25,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        {isOriginator ? (
          <EmptyHint text="Originator — no inbound bags. Drivers will pull morning departure bags from the bag room." />
        ) : crew.length === 0 ? (
          <EmptyHint text="No drivers assigned to this download yet." />
        ) : crew.map(run => (
          <DownloadRunCard key={run.runId} run={run} />
        ))}
      </Box>
    </Box>
  );
}

function DownloadRunCard({ run }) {
  const roleColor = run.splitType === 'FWD' ? C.fwd
                  : run.splitType === 'AFT' ? C.aft
                  : C.accent;
  return (
    <Box sx={{
      backgroundColor: 'rgba(30, 30, 30, 0.55)',
      border: `1px solid ${C.border}`,
      borderRadius: 1,
      p: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Box sx={{
          backgroundColor: `${roleColor}33`,
          color: roleColor,
          px: 0.75, py: 0.2,
          borderRadius: 0.5,
          fontSize: '0.7rem', fontWeight: 'bold',
          minWidth: 38, textAlign: 'center',
        }}>
          {run.slotLabel}
        </Box>
        <Typography sx={{
          fontSize: '0.85rem', color: C.textPrimary, fontWeight: 'bold',
          flex: 1, minWidth: 0,
        }} noWrap>
          {run.driver ? run.driver.displayName : (
            <Box component="span" sx={{ color: C.hot }}>Unassigned</Box>
          )}
        </Typography>
        {run.driver && (
          <Typography sx={{ fontSize: '0.7rem', color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}>
            ({run.driver.seniority})
          </Typography>
        )}
        <Typography sx={{ fontSize: '0.78rem', color: C.textSecondary, fontWeight: 'bold' }}>
          {run.isFreightOnly ? 'Freight' : `${run.totalBags} bags`}
        </Typography>
      </Box>
      {!run.isFreightOnly && run.stops?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {run.stops.map((stop, i) => (
            <Box key={i} sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              px: 0.6, py: 0.2,
              backgroundColor: 'rgba(76, 175, 80, 0.10)',
              border: `1px solid rgba(76, 175, 80, 0.3)`,
              borderRadius: 0.5,
              fontSize: '0.72rem',
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: C.ok, fontWeight: 'bold' }}>
                {stop.destination}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: C.textMuted }}>
                {stop.gate}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontWeight: 'bold' }}>
                {stop.bags} bags
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Right column: departure side ──────────────────────────────────

function DepartureColumn({ inbound, feeders, isTerminator }) {
  const totalFeederBags = feeders.reduce((t, f) => t + (f.bags || 0), 0);
  return (
    <Box sx={{
      flex: 1, minWidth: 0,
      display: 'flex', flexDirection: 'column',
    }}>
      <ColumnHeader
        icon={<FlightTakeoffIcon sx={{ fontSize: 18, color: C.cold }} />}
        title="Departure"
        subtitle={isTerminator
          ? 'Terminator — aircraft RONs tonight, no outbound'
          : `${feeders.length} transfer source${feeders.length === 1 ? '' : 's'} · ${totalFeederBags} bag${totalFeederBags === 1 ? '' : 's'} feeding`}
      />
      <Box sx={{
        flex: 1, overflow: 'auto', p: 1.25,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        {isTerminator ? (
          <EmptyHint text="Terminator — no outbound. Aircraft stays at the gate until tomorrow's originator." />
        ) : feeders.length === 0 ? (
          <EmptyHint text="No transfer bags coming in. Local bags only." />
        ) : feeders.map((f, i) => (
          <FeederCard key={`${f.inboundId}-${f.runId}-${i}`} feeder={f} />
        ))}
      </Box>
    </Box>
  );
}

function FeederCard({ feeder }) {
  const num = (feeder.inboundFlightNumber || '').replace(/^WN\s*/, '');
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      backgroundColor: 'rgba(30, 30, 30, 0.55)',
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${C.cold}`,
      borderRadius: 1,
      px: 1, py: 0.75,
    }}>
      <Box sx={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '0.78rem', fontWeight: 'bold',
        color: C.cold,
        backgroundColor: 'rgba(2, 119, 189, 0.15)',
        border: `1px solid rgba(2, 119, 189, 0.4)`,
        px: 0.6, py: 0.2,
        borderRadius: 0.5,
        minWidth: 42, textAlign: 'center',
      }}>
        {feeder.inboundGate}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.82rem', color: C.textPrimary, fontWeight: 'bold' }}>
          WN {num}
          <Box component="span" sx={{ color: C.textMuted, fontWeight: 'normal', ml: 0.5 }}>
            from {feeder.inboundFrom}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', color: C.textMuted }}>
          ETA {feeder.inboundEta} · {feeder.runSlotLabel}
          {feeder.driver
            ? <> · driver <Box component="span" sx={{ color: C.textSecondary, fontWeight: 'bold' }}>{feeder.driver.displayName}</Box></>
            : <Box component="span" sx={{ color: C.hot }}> · driver unassigned</Box>}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.85rem', color: C.textPrimary, fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
        {feeder.bags} bags
      </Typography>
    </Box>
  );
}

// ── Tiny shared helpers ──────────────────────────────────────────

function ColumnHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1,
      px: 1.5, py: 0.75,
      borderBottom: `1px solid ${C.border}`,
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      flexShrink: 0,
    }}>
      {icon}
      <Typography sx={{
        fontSize: '0.78rem', fontWeight: 'bold', color: C.textPrimary,
        letterSpacing: '0.08em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.72rem', color: C.textMuted }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

function EmptyHint({ text }) {
  return (
    <Box sx={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      px: 2, py: 4, textAlign: 'center',
    }}>
      <Typography sx={{ fontSize: '0.8rem', color: C.textMuted, fontStyle: 'italic' }}>
        {text}
      </Typography>
    </Box>
  );
}
